import { lazy, type FC } from 'react';
import { useMemoOne } from 'use-memo-one';
import type { CapacityGraphRequestParams } from 'src/apis/direct-fcfs/types';
import { useDirectFcfsCapacityGraph } from 'src/apis/direct-fcfs/use-direct-fcfs-capacity-graph';
import {
  useDirectFcfsCapacityPools,
  type DirectFcfsCapacityPoolsRequest,
} from 'src/apis/direct-fcfs/use-direct-fcfs-capacity-pools';
import type { NetworkPoint } from 'src/apis/monolith/types';
import type { NetworkPointRequest } from 'src/apis/monolith/use-network-point';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useCapacityOverviewPageParams } from 'src/pages/transport/network-points/details/capacity-overview-fcfs/use-page-params';

const Page = lazy(() => import('./page'));

const Content: FC<{
  networkPoint: NetworkPoint;
}> = ({ networkPoint }) => {
  const capacityPools = useDirectFcfsCapacityPools(
    networkPoint.networkPointUuid
  );

  if (!capacityPools?.response) return <PageSpinner />;

  return (
    <ContentWithPools
      networkPoint={networkPoint}
      capacityPools={capacityPools}
    />
  );
};

const ContentWithPools: FC<{
  networkPoint: NetworkPoint;
  capacityPools: Successful<DirectFcfsCapacityPoolsRequest>;
}> = ({ networkPoint, capacityPools }) => {
  const pageParams = useCapacityOverviewPageParams(capacityPools.response.data);

  const paramsForGraph = useMemoOne<CapacityGraphRequestParams>(() => {
    return {
      validFrom: pageParams.value.runtimeIntersectionFilterStart,
      validTo: pageParams.value.runtimeIntersectionFilterEnd,
      capacityPoolId: pageParams.value.capacityPoolId,
    };
  }, [pageParams.value]);

  const nwpCapacityGraph = useDirectFcfsCapacityGraph(
    networkPoint.networkPointUuid,
    paramsForGraph
  );

  if (!nwpCapacityGraph?.response) return <PageSpinner />;

  return (
    <Page
      networkPoint={networkPoint}
      pageParams={pageParams}
      capacityPools={capacityPools}
      nwpCapacityGraph={nwpCapacityGraph}
    />
  );
};

export const CapacityOverviewFcfs: FC<{
  networkPoint: NetworkPointRequest;
}> = ({ networkPoint }) => {
  if (!networkPoint.response) return <PageSpinner />;
  return <Content networkPoint={networkPoint.response.data} />;
};
